import React from 'react';
import { Switch, Redirect } from 'react-router-dom';

import {
    RouteWithLayout,
    PrivateRouteWithLayout,
    PreAuthRouteWithLayout
} from './components';

import {
    Main as MainLayout,
    Minimal as MinimalLayout,
} from './layouts';

import {
    Login as LoginPage,
    GuestLogin as GuestLoginPage,
    Logout as LogoutPage,
    Register as RegisterPage,
    InitPasswordReset as InitPasswordResetPage,
    PasswordReset as PasswordResetPage,
    ChangePassword as ChangePasswordPage,
    ContactUsPage,
    AccountInformationPage,
    ConfirmEmail as ConfirmEmailPage,
    PersistGuestAccount as PersistGuestAccountPage,
    NotFound as NotFoundPage,

    WelcomeAndShowProductsPage,
    DqGuideShowPage,
    ProgramRatingMainPage,
    ProgramCreationMainPage,
    ProgramDashboardMainPage,
    ProgramOutlineMainPage,
    CompanyProgramInfoMainPage,
    RatingContentReportMainPage,
    RatedProgramMainPage,
    DQGuidePage,
    TopRatedProgramsPage,
    RatedProgramsDQAssessmentListPage,
    RatedProgramsListPage,
    RatingAccessAssessmentPage,
    RatingAssessmentWelcomePage,
    RatingSelectAssessmentPage,
    CPCRatingPaymentPage,
    DQRatingPaymentPage,
    CPCRatingFullReportPage,
    DQRatingFullReportPage,
    CPCRatingContentReportPage,
    DQRatingContentReportPage,
    CPCRatedProgramPage,
    DQRatedProgramPage,
    CPCCompanyProgramInfoPage,
    DQCompanyProgramInfoPage,
    CPCProgramRatingPage,
    DQProgramRatingPage,
    RatingReportTypesPage,
    ProgramUpgradePage,
    ProgramCurationEnablePage,
    ProgramCurationPreviewPage,
    ProgramResourcesPage,
    DynamicLearningPage,
    ProgramPreviewPage,
    ProgramEditPage,
    ProgramsListPage,
    HomePage,
    ProgramOutlinePage,
    ProgramDashboardPage,
    ProgramCreationPage,
    FrameworksListPage,
    FrameworkCreationPage,
    AssessmentCreationPage
} from './pages';

import DqSubscriptionPage from "./pages/Public/DqSubscriptionPlans.page";

const preAuthRoutes = [
    { path: '/login', component: LoginPage },
    { path: '/guest_login', component: GuestLoginPage },
    { path: '/register', component: RegisterPage },
    { path: '/account/recover', component: InitPasswordResetPage },
    { path: '/auth/reset/password', component: PasswordResetPage, no_redirect: true },

    { path: '/welcome', component: WelcomeAndShowProductsPage },
    { path: '/program-rating', component: ProgramRatingMainPage },
    { path: '/dq-guide', component: DqGuideShowPage },
    { path: '/program-creator', component: ProgramCreationMainPage },
    { path: '/program-dashboard', component: ProgramDashboardMainPage },
    { path: '/program-outline', component: ProgramOutlineMainPage },
    // { path: '/subscribe', component: DqSubscriptionPage },
    { path: '/rating/program', component: RatedProgramMainPage },
    { path: '/rating/content_report', component: RatingContentReportMainPage },
    { path: '/rating/company-program-info', component: CompanyProgramInfoMainPage },
];

const privateRoutes = [
    { path: '/auth/confirm/email', component: ConfirmEmailPage, layout: MinimalLayout },
    { path: '/auth/persist_guest_account', component: PersistGuestAccountPage },
    { path: '/password/change', component: ChangePasswordPage },
    { path: '/contact-us', component: ContactUsPage },
    { path: '/account/information', component: AccountInformationPage },
    { path: '/logout', component: LogoutPage },

    // {path: '/program_creation', component: ProgramCreation},
    // {path: '/program_details', component: ProgramDetails},
    // {path: '/program_overview', component: ProgramOutline},
    {path: '/home', component: HomePage },

    // Program Creator Routes
    {path: '/program/new', component: ProgramCreationPage},   // Page to create a new program
    {path: '/programs/list', component: ProgramsListPage}, // List of programs

    {path: '/program/:programId/dashboard', component: ProgramDashboardPage},  // Dashboard for a specific program
    {path: '/program/:programId/outline', component: ProgramOutlinePage},      // Outline view for a specific program
    {path: '/program/:programId/edit', component: ProgramEditPage},            // Edit page for a specific program
    {path: '/program/:programId/preview', component: ProgramPreviewPage},      // Preview page for a specific program
    {path: '/program/:programId/dynamic_learning', component: DynamicLearningPage},  // Dynamic learning component for the program
    {path: '/program/:programId/curation-enable', component: ProgramCurationEnablePage},  // Curation Enable view for the program
    {path: '/program/:programId/curation-preview', component: ProgramCurationPreviewPage},  // Curation Preview view for the program
    {path: '/program/:programId/resources', component: ProgramResourcesPage},  // Resources view for the program
    {path: '/program/:programId/upgrade', component: ProgramUpgradePage},  // Upgrade view for the program

    // CPC Stars Rating Routes
    {path: '/dq_stars_rating/program/new', component: CPCProgramRatingPage},  // New Rating Program
    {path: '/rating/program/welcome', component: RatingReportTypesPage},  // List of Report Types
    {path: '/dq_stars_rating/:programId/company-program-info', component: CPCCompanyProgramInfoPage},
    // { path: '/rating/:programId/program-info', component: ProgramInfo, layout: RatingLayout },
    {path: '/dq_stars_rating/:programId', component: CPCRatedProgramPage},  // Rating details for a specific program
    {path: '/dq_stars_rating/:programId/content_report', component: CPCRatingContentReportPage},  // Content report for a specific program's rating
    {path: '/dq_stars_rating/:programId/payment', component: CPCRatingPaymentPage},  // Payment Page
    {path: '/rating/:programId/welcome_assessment', component: RatingAssessmentWelcomePage},  // Assessment Welcome Page
    {path: '/rating/:programId/select_assessment', component: RatingSelectAssessmentPage},  // Select Assessment Page
    {path: '/rating/:programId/access_assessment', component: RatingAccessAssessmentPage},  // Select Assessment Page
    {path: '/dq_stars_rating/:programId/full_report', component: CPCRatingFullReportPage},  // Rating Programs List
    {path: '/rating/programs/top', component: TopRatedProgramsPage},  // Top-rated programs page
    {path: '/rating/programs/list', component: RatedProgramsListPage},  // Rating Programs List
    {path: '/rating/programs/assessment/list', component: RatedProgramsDQAssessmentListPage},  // Rating DQ Assessment Programs List

    // DQ Rating Routes
    {path: '/dq_rating/program/new', component: DQProgramRatingPage},
    {path: '/dq_rating/:programId/company-program-info', component: DQCompanyProgramInfoPage},
    {path: '/dq_rating/:programId', component: DQRatedProgramPage},
    {path: '/dq_rating/:programId/content_report', component: DQRatingContentReportPage},
    {path: '/dq_rating/:programId/payment', component: DQRatingPaymentPage},
    {path: '/dq_rating/:programId/full_report', component: DQRatingFullReportPage},

    //FrameWorks Routes
    {path: '/frameworks/list', component: FrameworksListPage},  // FrameWorks List
    {path: '/framework/new', component: FrameworkCreationPage},  // New Framework Creation

    //Assessment Routes
    {path: '/assessment/new', component:AssessmentCreationPage},  //New Assessment

    // DQ Guide Routes
    {path: '/dq_guide', component: DQGuidePage},  // DQ Guide page
];

const Routes = () => {
    return (
        <Switch>
            <Redirect exact from="/" to="/login" />
            {preAuthRoutes.map((route, index) => (
                <PreAuthRouteWithLayout
                    key={index}
                    exact
                    layout={MinimalLayout}
                    {...route}
                />
            ))}
            {privateRoutes.map((route, index) => (
                <PrivateRouteWithLayout
                    key={index}
                    exact
                    layout={route.layout || MainLayout}
                    {...route}
                />
            ))}
            <RouteWithLayout
                component={NotFoundPage}
                exact={false}
                layout={MinimalLayout}
                path="/"
            />
        </Switch>
    );
};

export default Routes;